import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, CheckCircle2, Mail } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SEO } from "@/components/SEO";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="silk-hero min-h-[70vh] px-5 sm:px-8 py-16">
      <SEO title="Contact — O.R.E.C" description="Questions about O.R.E.C, bookings, or joining as a practitioner? Send The Team a message." />
      <div className="max-w-xl mx-auto" data-testid="contact-page">
        {sent ? (
          <div className="silk-card rounded-lg p-10 text-center" data-testid="contact-success">
            <CheckCircle2 size={40} className="text-primary mx-auto mb-4" />
            <h1 className="font-display text-3xl text-foreground mb-3">Message sent ✦</h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Thank you for reaching out — The Team reads every message personally and will reply to your email soon.
            </p>
          </div>
        ) : (
          <>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-3">Contact</p>
            <h1 className="font-display text-4xl sm:text-5xl text-foreground mb-3">Say hello</h1>
            <p className="text-sm text-muted-foreground mb-10 leading-relaxed">
              Questions about a booking, joining as a practitioner, or anything else — send a note and The Team will get back to you.
            </p>
            {!showForm ? (
              <Button data-testid="contact-team-btn" onClick={() => setShowForm(true)} className="rounded-full px-8 h-12">
                <Mail size={15} className="mr-2" /> Contact Team
              </Button>
            ) : (
            <form onSubmit={submit} className="silk-card rounded-lg p-8 space-y-5">
              <div>
                <Label htmlFor="c-name" className="text-xs uppercase tracking-wider text-muted-foreground">Your name</Label>
                <Input id="c-name" data-testid="contact-name-input" required value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="c-email" className="text-xs uppercase tracking-wider text-muted-foreground">Your email</Label>
                <Input id="c-email" data-testid="contact-email-input" type="email" required value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="c-message" className="text-xs uppercase tracking-wider text-muted-foreground">Message</Label>
                <Textarea id="c-message" data-testid="contact-message-input" required rows={6} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2"
                  placeholder="How can we help?" />
              </div>
              <Button type="submit" disabled={busy} className="rounded-full px-7" data-testid="contact-submit-btn">
                <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Send message"}
              </Button>
            </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
